'use client';
import { ImageStrapiProps } from '@/decodo/components/atoms/Image/ImageStrapi';
import { AccordionItemProps } from '@/decodo/components/molecules/accordion/AccordionItem';
import AccordionList from '@/decodo/components/molecules/accordion/AccordionList';
import React, { useEffect, useRef, useState } from 'react';
import { ProductCategoryTitlesProps } from './comparisonUtils/comparisonElements/ProductCategory';
import { ComparisonGridRow } from './comparisonUtils/comparisonSections/ComparisonRow';
import { ComparisonGridHeader } from './comparisonUtils/comparisonSections/DesktopComparisonGridHeader';
import { ComparisonFeatureRows } from './comparisonUtils/comparisonSections/DesktopFeatureRow';
import { PriceActionRow } from './comparisonUtils/comparisonSections/PriceActionRow';
import { ProductComparisonItemProps } from './ProductComparisonTable';

export interface ProductComparisonTableProps {
  selectedData: ProductComparisonItemProps[];
  productTypeCategoryTitle?: string;
  productTypeCategoryTooltip?: React.ReactNode;
  keyFeaturesTitle?: string;
  keyFeaturesTooltip?: React.ReactNode;
  descriptionTitle?: string;
  descriptionTooltip?: React.ReactNode;
  productFeaturesCategoryTitles?: ProductCategoryTitlesProps[];
  productComparisonItems?: ProductComparisonItemProps[];
  accordionContent?: AccordionItemProps[];
  isBestValue?: boolean;
  productTitle?: string;
  productIcon?: ImageStrapiProps;
  stickyOffsetTop?: number;
}

export function DesktopComparisonGrid({
  selectedData,
  productTypeCategoryTitle,
  productTypeCategoryTooltip,
  keyFeaturesTitle,
  keyFeaturesTooltip,
  descriptionTitle,
  descriptionTooltip,
  productFeaturesCategoryTitles,
  accordionContent,
  stickyOffsetTop,
}: ProductComparisonTableProps) {
  const [headerHeights, setHeaderHeights] = useState<number[]>([0, 0, 0]);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);

  const headerTopMarginOffset = 64;

  useEffect(() => {
    const heights = headerRefs.current.map((el) => el?.offsetHeight || 0);
    setHeaderHeights(heights);
  }, [selectedData]);

  const filteredData = selectedData.filter(Boolean);
  const visibleCount = filteredData.length;

  return (
    <>
      <div>
        <div
          className='z-10'
          style={{
            position: 'sticky',
            top: (stickyOffsetTop ?? 0) + headerTopMarginOffset,
          }}
        >
          <ComparisonGridHeader
            selectedData={selectedData}
            headerRefs={headerRefs}
            stickyOffsetTop={stickyOffsetTop}
          />
        </div>

        <ComparisonGridRow
          title={productTypeCategoryTitle}
          tooltip={productTypeCategoryTooltip}
          cellContents={filteredData.map((d) => d?.productType?.productType)}
          visibleItemsCount={visibleCount}
        />

        <ComparisonGridRow
          title={keyFeaturesTitle}
          tooltip={keyFeaturesTooltip}
          cellContents={filteredData.map((d) => d?.productType?.features)}
          visibleItemsCount={visibleCount}
        />

        <ComparisonGridRow
          title={descriptionTitle}
          tooltip={descriptionTooltip}
          cellContents={filteredData.map((d) => d?.productType?.description)}
          visibleItemsCount={visibleCount}
        />
        <div
          className='z-10'
          style={{
            position: 'sticky',
            top: (stickyOffsetTop ?? 0) + 160,
          }}
        >
          <PriceActionRow
            selectedData={selectedData}
            headerHeights={headerHeights}
            stickyOffsetTop={stickyOffsetTop}
          />
        </div>
        <ComparisonFeatureRows
          titles={productFeaturesCategoryTitles}
          selectedData={selectedData}
        />

        {accordionContent && (
          <div className='mt-12'>
            <AccordionList
              accordionList={accordionContent}
              isMultiMode={true}
            />
          </div>
        )}
      </div>
    </>
  );
}
